import React, { useEffect, useRef, useState } from 'react';
import ToggleButtons from '../../components/Shared/ToggleButtons/ToggleButtons';
import QuillInput from '../../components/Shared/QuillInput/QuillInput';
import { FAQService } from '../../services/FAQSerivce';
import { showErrorToast, showSuccessToast } from '../../components/Shared/Toastify/toastHelper';
import { CircularProgress } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const label = [
  { label_name: "English", index: 0 },
  { label_name: "French", index: 1 },
  { label_name: "Arabic", index: 2 }
];

interface props {
  mode: 'add' | 'edit' | 'view'
}

function FAQForm({ mode }: props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeButton, setActiveButton] = useState(0);
  const [localQuestions, setLocalQuestions] = useState({
    eng: "",
    fr: "",
    ar: ""
  });
  const [localAnswers, setLocalAnswers] = useState({
    eng: "",
    fr: "",
    ar: ""
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && location.state?.faq) {
      const { faq } = location.state;
      setLocalQuestions({
        eng: faq.title.eng || "",
        fr: faq.title.fr || "",
        ar: faq.title.ar || ""
      });
      setLocalAnswers({
        eng: faq.answer.eng || "",
        fr: faq.answer.fr || "",
        ar: faq.answer.ar || ""
      });
    }
  }, [mode, location.state]);

  const validateForm = (): boolean => {
    const missingFields: string[] = [];
    
    // Check English fields
    if (!localQuestions.eng?.trim()) missingFields.push('English Question');
    if (!localAnswers.eng?.trim()) missingFields.push('English Answer');
    
    // Check French fields
    if (!localQuestions.fr?.trim()) missingFields.push('French Question');
    if (!localAnswers.fr?.trim()) missingFields.push('French Answer');
    
    // Check Arabic fields
    if (!localQuestions.ar?.trim()) missingFields.push('Arabic Question');
    if (!localAnswers.ar?.trim()) missingFields.push('Arabic Answer');

    if (missingFields.length > 0) {
      showErrorToast(`The following fields are missing: ${missingFields.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSetActiveButton = (value: number) => {
    setActiveButton(value);
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const language = activeButton === 0 ? 'eng' : activeButton === 1 ? 'fr' : 'ar';
    setLocalQuestions(prev => ({
      ...prev,
      [language]: e.target.value
    }));
  };

  const handleAnswerChange = (value: string) => {
    const language = activeButton === 0 ? 'eng' : activeButton === 1 ? 'fr' : 'ar';
    setLocalAnswers(prev => ({
      ...prev,
      [language]: value
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (mode === 'add') {
        await FAQService.createFAQ(localQuestions, localAnswers);
        showSuccessToast("FAQ created successfully.");
      } else if (mode === 'edit' && id) {
        await FAQService.updateFAQ(id, localQuestions, localAnswers);
        showSuccessToast("FAQ updated successfully.");
      }
      navigate('/faq');
    } catch (error: any) {
      showErrorToast(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/faq');
  };

  const getCurrentQuestion = () => {
    switch (activeButton) {
      case 0:
        return localQuestions.eng;
      case 1:
        return localQuestions.fr;
      case 2:
        return localQuestions.ar;
      default:
        return '';
    }
  };

  const getCurrentAnswer = () => {
    switch (activeButton) {
      case 0:
        return localAnswers.eng;
      case 1:
        return localAnswers.fr;
      case 2:
        return localAnswers.ar;
      default:
        return '';
    }
  };

  return (
    <div className='flex flex-col'>
      <ToggleButtons
        labels={label}
        activeButton={activeButton}
        setActiveButton={handleSetActiveButton}
        
      />

      <div className='my-[40px] bg-white rounded-[16px] pb-[32px] pt-[16px] px-[24px] flex flex-col gap-[32px]'>
        <p className='font-semibold text-[20px]'>Question</p>
        <div>
          <label htmlFor='Title'>Title</label>
          <input
            type="text"
            id="Title"
            className='w-full border border-[#E0E0E0] rounded-[8px] px-[16px] py-[8px] mt-[4px]'
            placeholder='Enter Section Title'
            value={getCurrentQuestion()}
            onChange={handleQuestionChange}
            disabled={mode === 'view'}
          />
        </div>
      </div>

      <QuillInput 
        key={`quill-${activeButton}`}
        title='Answer' 
        value={getCurrentAnswer()} 
        onChange={handleAnswerChange}
        disabled={mode === 'view'}
      />
      
      <div className='mt-[40px] ml-auto flex gap-4'>
        <button 
          onClick={handleBack} 
          className='bg-gray-200 rounded-[8px] h-[48px] w-[140px] text-[14px] font-medium text-gray-700'
        >
          BACK
        </button>
        {mode !== 'view' && (
          <button 
            onClick={handleSave} 
            className='bg-[#00726B] rounded-[8px] h-[48px] w-[140px] text-[14px] font-medium text-white'
          >
            {loading ? <CircularProgress /> : "SAVE"}
          </button>
        )}
      </div>
    </div>
  );
}

export default FAQForm;
